import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Layout } from "../components/layout/Layout";
import { User, Meeting, ClinicUserApiResponse, MeetingType, MedicationType } from "../types/api";
import apiService from "../services/api";
import { ContextMenu } from "../components/ui/ContextMenu";
import { FinishTreatmentModal } from "../components/modals/FinishTreatmentModal";
import { getMeetingsForDayWithLayout, LAYOUT_CONFIG } from "../utils/calendarLayout";
import { getMeetingTypeName, shouldStartRecallBot } from "../utils/meetingUtils";
import { POPUP_STYLES } from "../constants/popupStyles";
import { NoShowModal } from "../components/modals/NoShowModal";
import { CancelMeetingModal } from "../components/modals/CancelMeetingModal";
import { CallPatientModal } from "../components/modals/CallPatientModal";
import { WhatsAppConversationPopup } from "../components/whatsapp/WhatsAppConversationPopup";
import { normalizePhone } from "../utils/phoneUtils";

interface EditCommentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (comments: string) => Promise<void>;
    currentComments: string;
    isSaving: boolean;
}

interface CalendarScreenProps {
    user: User;
    onLogout: () => void;
    onNavigate?: (screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections" | "billing" | "whatsapp", patientId?: string | number, meetingId?: string | number) => void;
    users: ClinicUserApiResponse[];
    medicationTypes: MedicationType[];
    meetingTypes: MeetingType[];
    clinics?: any;
    taskCount?: number;
    whatsappUnreadCount?: number;
}

// Selectable meeting statuses for the calendar filter. Values are the
// lower-cased strings sent to the backend as `status=` query params.
const STATUS_OPTIONS: { value: string; label: string }[] = [
    { value: "active", label: "Active" },
    { value: "done", label: "Done" },
    { value: "noshow", label: "Noshow" },
    { value: "canceled", label: "Canceled" },
];

export const CalendarScreen: React.FC<CalendarScreenProps> = ({
    user,
    onLogout,
    onNavigate,
    users: propsUsers,
    medicationTypes: propsMedicationTypes,
    meetingTypes: propsMeetingTypes,
    clinics: propsClinics,
    taskCount,
    whatsappUnreadCount,
}) => {
    // Calculate whether user is admin before state initialization
    const isAdmin = useMemo(() => {
        return user?.role === 'admin' || user?.role === 'Admin';
    }, [user?.role]);

    // Calculate default "only mine" value based on user role and type
    const defaultOnlyMy = useMemo(() => {
        console.log('[CalendarScreen] User object:', user);
        console.log('[CalendarScreen] User type:', user.type);
        console.log('[CalendarScreen] User role:', user.role);
        const isAdminRole = user?.role === 'admin' || user?.role === 'Admin';
        const isCaseManagerOrDoctor = user.type === 'doctor' || user.type === 'case_manager';
        const shouldDefaultToTrue = isAdminRole && isCaseManagerOrDoctor;
        console.log('[CalendarScreen] Should default onlyMy to:', shouldDefaultToTrue);
        return shouldDefaultToTrue;
    }, [user]);

    // Initialize date from URL query parameter
    const getDateFromURL = (): Date => {
        const params = new URLSearchParams(window.location.search);
        const dateParam = params.get('date');
        if (dateParam) {
            const parsedDate = new Date(dateParam);
            if (!isNaN(parsedDate.getTime())) {
                return parsedDate;
            }
        }
        return new Date(); // default to today
    };

    const [currentDate, setCurrentDate] = useState(getDateFromURL);
    const [meetings, setMeetings] = useState<Meeting[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Filter states - initialize with correct values to prevent cascading updates
    const [meetingTypeId, setMeetingTypeId] = useState(0);
    const [clinicId, setClinicId] = useState(0);
    const [userId, setUserId] = useState(0);
    // Status filter: array of lower-cased status values to send (each as its
    // own `status=` query param). An empty array means "All Statuses", in which
    // case the status parameter is omitted entirely.
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const statusMenuRef = useRef<HTMLDivElement>(null);

    const toggleStatus = (value: string) => {
        setSelectedStatuses((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    // Close the status checkbox dropdown when clicking outside of it.
    useEffect(() => {
        if (!statusMenuOpen) return;
        const handler = (e: MouseEvent) => {
            if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
                setStatusMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [statusMenuOpen]);
    // For non-admins, always set onlyMy to false; for admins, use defaultOnlyMy
    const [onlyMy, setOnlyMy] = useState(isAdmin ? defaultOnlyMy : false);

    // Initialize view from URL query parameter
    const getViewFromURL = (): "month" | "week" | "3days" | "day" => {
        const params = new URLSearchParams(window.location.search);
        const urlView = params.get('view');
        if (urlView === 'month' || urlView === 'week' || urlView === '3days' || urlView === 'day') {
            return urlView as "month" | "week" | "3days" | "day";
        }
        return 'day'; // default
    };

    const [view, setView] = useState<"month" | "week" | "3days" | "day">(getViewFromURL);

    // Selection and context menu states
    const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; meetingId: number } | null>(null);
    const [pendingAction, setPendingAction] = useState<{ action: string; meetingId: number } | null>(null);
    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'loading'; text: string } | null>(null);
    const [isFinishTreatmentModalOpen, setIsFinishTreatmentModalOpen] = useState(false);
    const [selectedPatientIdForFinishTreatment, setSelectedPatientIdForFinishTreatment] = useState<number | null>(null);

    // Edit Comments state
    const [isEditCommentsModalOpen, setIsEditCommentsModalOpen] = useState(false);
    const [selectedMeetingForComments, setSelectedMeetingForComments] = useState<Meeting | null>(null);
    const [isSavingComments, setIsSavingComments] = useState(false);

    // No Show Modal state
    const [isNoShowModalOpen, setIsNoShowModalOpen] = useState(false);
    const [selectedMeetingForNoShow, setSelectedMeetingForNoShow] = useState<Meeting | null>(null);

    // Cancel Meeting Modal state
    const [isCancelMeetingModalOpen, setIsCancelMeetingModalOpen] = useState(false);
    const [selectedMeetingForCancel, setSelectedMeetingForCancel] = useState<Meeting | null>(null);
    const [selectedMeetingForCall, setSelectedMeetingForCall] = useState<Meeting | null>(null);
    const [waPopupPatient, setWaPopupPatient] = useState<{ phone: string; name?: string; hmId?: string; id?: number } | null>(null);

    // Available slots state
    const [availableSlots, setAvailableSlots] = useState<Array<{ start: string; end: string }>>([]);

    // Ref to prevent double-fetching in React Strict Mode
    const meetingsParamsRef = useRef<string>("");
    const slotsParamsRef = useRef<string>("");

    // Users list for dropdown - using props
    const users = propsUsers;
    const meetingTypes = propsMeetingTypes; // Use meeting types from props

    const token = localStorage.getItem("token") || "";

    // Use clinics from props
    const clinics = useMemo(
        () => propsClinics?.items || [],
        [propsClinics?.items]
    );

    // Update URL when view changes
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        params.set('view', view);
        // Format date as YYYY-MM-DD
        const dateStr = currentDate.toISOString().split('T')[0];
        params.set('date', dateStr);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        if (window.location.href !== window.location.origin + newUrl) {
            window.history.pushState({}, '', newUrl);
        }
    }, [view, currentDate]);

    // Handle browser back/forward for view changes
    React.useEffect(() => {
        const handlePopState = () => {
            setView(getViewFromURL());
            setCurrentDate(getDateFromURL());
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Helper to get start of week (Sunday)
    const getWeekStart = (date: Date) => {
        const d = new Date(date);
        d.setDate(d.getDate() - d.getDay());
        d.setHours(0, 0, 0, 0);
        return d;
    };

    // Helper to get week days
    const getWeekDays = (startDate: Date) => {
        const days: Date[] = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(startDate);
            day.setDate(startDate.getDate() + i);
            days.push(day);
        }
        return days;
    };

    // Helper to get 3 days starting from current date
    const getThreeDays = (startDate: Date) => {
        const days: Date[] = [];
        for (let i = 0; i < 3; i++) {
            const day = new Date(startDate);
            day.setDate(startDate.getDate() + i);
            days.push(day);
        }
        return days;
    };

    // Convert available slots to meeting-like objects
    const convertSlotsToMeetings = useCallback((): Meeting[] => {
        return availableSlots.map((slot, index) => ({
            id: -100000 - index, // Negative IDs to distinguish from real meetings
            start: new Date(slot.start).getTime(),
            end: new Date(slot.end).getTime(),
            patient_name: "Availability",
            HM_ID: "Available",
            user_id: userId !== 0 ? userId : user.id,
            status: "available",
            color: "#e5e7eb", // Gray color for availability
            meetingtype_id: 0,
            patient_id: 0,
            notes: "",
            comments: "",
            warning: "",
            patient_type: "",
            re_intake: false,
            summary_ready: false,
            doctor_signature: null,
            therapist_name: "",
            cm_summary_ready: false,
            link: "",
            clinic_id: 0,
            prep_status: "",
            team_url: "",
            yoman_id: 0,
            therapist_signature: null,
            cm_full_name: "",
            assigned_to: 0,
            finished: false
        } as unknown as Meeting));
    }, [availableSlots, userId, user.id]);

    // Combine regular meetings with availability slots
    const allMeetingsWithSlots = useMemo(() => {
        return [...meetings, ...convertSlotsToMeetings()];
    }, [meetings, convertSlotsToMeetings]);

    // Calculate hour range based on meetings visible in current view
    const getHourRange = useMemo(() => {
        // Always show at least 8 AM - 6 PM
        const MIN_HOUR = 8;
        const MAX_HOUR = 18;

        // Filter meetings based on current view
        let visibleMeetings = meetings;

        if (view === 'day') {
            const dayStart = new Date(currentDate);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(currentDate);
            dayEnd.setHours(23, 59, 59, 999);

            visibleMeetings = meetings.filter(meeting =>
                meeting.start >= dayStart.getTime() && meeting.start <= dayEnd.getTime()
            );
        } else if (view === '3days') {
            const threeStart = new Date(currentDate);
            threeStart.setHours(0, 0, 0, 0);
            const threeEnd = new Date(currentDate);
            threeEnd.setDate(threeEnd.getDate() + 3);
            threeEnd.setHours(23, 59, 59, 999);

            visibleMeetings = meetings.filter(meeting =>
                meeting.start >= threeStart.getTime() && meeting.start <= threeEnd.getTime()
            );
        } else if (view === 'week') {
            const weekStart = getWeekStart(currentDate);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekEnd.getDate() + 7);
            weekEnd.setHours(23, 59, 59, 999);

            visibleMeetings = meetings.filter(meeting =>
                meeting.start >= weekStart.getTime() && meeting.start <= weekEnd.getTime()
            );
        }

        if (visibleMeetings.length === 0) {
            // Default to 8 AM - 6 PM if no meetings
            return Array.from({ length: MAX_HOUR - MIN_HOUR + 1 }, (_, i) => i + MIN_HOUR);
        }

        const startHours = visibleMeetings.map(meeting => {
            const date = new Date(meeting.start);
            return date.getHours();
        });

        const endHours = visibleMeetings.map(meeting => {
            const date = new Date(meeting.end);
            return date.getHours();
        });

        const allHours = [...startHours, ...endHours];

        // Show at least 8-18, but extend if meetings are outside this range
        const minHour = Math.min(MIN_HOUR, Math.min(...allHours));
        const maxHour = Math.max(MAX_HOUR, Math.max(...allHours));

        return Array.from({ length: maxHour - minHour + 1 }, (_, i) => i + minHour);
    }, [meetings, view, currentDate]);

    // Helper to get hours array (dynamic based on meetings)
    const hours = getHourRange;

    // Get calendar grid data
    const calendarDays = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // First day of month
        const firstDay = new Date(year, month, 1);
        // Last day of month
        const lastDay = new Date(year, month + 1, 0);

        // Start from the Sunday before first day
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - startDate.getDay());

        // End on the Saturday after last day
        const endDate = new Date(lastDay);
        endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

        const days: Date[] = [];
        const current = new Date(startDate);

        while (current <= endDate) {
            days.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }

        return days;
    }, [currentDate]);

    // Calculate positioned meetings for all visible days (for absolute positioning layout)
    const positionedMeetingsForDays = useMemo(() => {
        const result: Record<string, any[]> = {};
        // Use the minimum hour from the dynamic hour range instead of hardcoded 8
        const dayStartHour = hours.length > 0 ? Math.min(...hours) : 8;

        if (view === 'week') {
            // For week view, calculate positioned meetings for each day
            const weekDays = getWeekDays(getWeekStart(currentDate));
            for (const day of weekDays) {
                const dayKey = day.toDateString();
                result[dayKey] = getMeetingsForDayWithLayout(
                    day,
                    allMeetingsWithSlots,
                    dayStartHour,
                    LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK
                );
            }
        } else if (view === '3days') {
            // For 3-day view, calculate positioned meetings for each day
            const threeDays = getThreeDays(currentDate);
            for (const day of threeDays) {
                const dayKey = day.toDateString();
                result[dayKey] = getMeetingsForDayWithLayout(
                    day,
                    allMeetingsWithSlots,
                    dayStartHour,
                    LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK
                );
            }
        } else if (view === 'day') {
            // For day view, calculate positioned meetings for the current day
            const dayKey = currentDate.toDateString();
            result[dayKey] = getMeetingsForDayWithLayout(
                currentDate,
                allMeetingsWithSlots,
                dayStartHour,
                LAYOUT_CONFIG.PIXELS_PER_HOUR_DAY
            );
        }

        return result;
    }, [allMeetingsWithSlots, view, currentDate, hours]);

    // Helper function to refresh meetings based on current view
    const refreshMeetings = useCallback(async () => {
        const token = localStorage.getItem("token") || "";
        let fromDate: Date;
        let toDate: Date;

        // Calculate date range based on current view
        if (view === "day") {
            fromDate = new Date(currentDate);
            fromDate.setHours(0, 0, 0, 0);
            toDate = new Date(currentDate);
            toDate.setHours(23, 59, 59, 999);
        } else if (view === "3days") {
            fromDate = new Date(currentDate);
            fromDate.setHours(0, 0, 0, 0);
            toDate = new Date(currentDate);
            toDate.setDate(toDate.getDate() + 2);
            toDate.setHours(23, 59, 59, 999);
        } else if (view === "week") {
            fromDate = getWeekStart(currentDate);
            toDate = new Date(fromDate);
            toDate.setDate(toDate.getDate() + 6);
            toDate.setHours(23, 59, 59, 999);
        } else { // month view
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            fromDate = new Date(year, month, 1);
            fromDate.setDate(fromDate.getDate() - fromDate.getDay()); // Start from Sunday of the first week
            toDate = new Date(year, month + 1, 0);
            toDate.setDate(toDate.getDate() + (6 - toDate.getDay())); // End on Saturday of the last week
            toDate.setHours(23, 59, 59, 999);
        }

        try {
            const response = await apiService.getMeetingsByDates({
                token,
                meeting_type_id: meetingTypeId,
                clinic_id: clinicId,
                user_id: userId,
                only_my: onlyMy,
                from_date: fromDate.getTime(),
                to_date: toDate.getTime(),
                status: selectedStatuses,
            });
            setMeetings(response);
        } catch (err) {
            console.error("Failed to fetch meetings:", err);
            setError("Failed to load meetings.");
        }
    }, [view, currentDate, meetingTypeId, clinicId, userId, onlyMy, selectedStatuses]);

    // Load meetings when date range or filters change
    React.useEffect(() => {
        // Create a unique key from current parameters to detect changes and prevent Strict Mode duplicates
        const currentParamsKey = JSON.stringify([
            view,
            currentDate.getTime(),
            meetingTypeId,
            clinicId,
            userId,
            onlyMy,
            selectedStatuses
        ]);

        // Skip if we've already loaded these exact parameters
        if (meetingsParamsRef.current === currentParamsKey) {
            return;
        }

        meetingsParamsRef.current = currentParamsKey;

        const token = localStorage.getItem("token") || "";
        let fromDate: Date;
        let toDate: Date;

        // Calculate date range based on current view
        if (view === "day") {
            fromDate = new Date(currentDate);
            fromDate.setHours(0, 0, 0, 0);
            toDate = new Date(currentDate);
            toDate.setHours(23, 59, 59, 999);
        } else if (view === "week") {
            const getWeekStart = (date: Date) => {
                const d = new Date(date);
                const day = d.getDay();
                const diff = d.getDate() - day;
                return new Date(d.setDate(diff));
            };
            fromDate = getWeekStart(currentDate);
            toDate = new Date(fromDate);
            toDate.setDate(toDate.getDate() + 6);
            toDate.setHours(23, 59, 59, 999);
        } else { // month view
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            fromDate = new Date(year, month, 1);
            fromDate.setDate(fromDate.getDate() - fromDate.getDay());
            toDate = new Date(year, month + 1, 0);
            toDate.setDate(toDate.getDate() + (6 - toDate.getDay()));
            toDate.setHours(23, 59, 59, 999);
        }

        const loadMeetings = async () => {
            setLoading(true);
            setError("");

            try {
                const response = await apiService.getMeetingsByDates({
                    token,
                    meeting_type_id: meetingTypeId,
                    clinic_id: clinicId,
                    user_id: userId,
                    only_my: onlyMy,
                    from_date: fromDate.getTime(),
                    to_date: toDate.getTime(),
                    status: selectedStatuses,
                });
                setMeetings(response);
            } catch (err: any) {
                console.error("Failed to load meetings:", err);
                setError("Failed to load meetings");
            } finally {
                setLoading(false);
            }
        };

        loadMeetings();
    }, [view, currentDate, meetingTypeId, clinicId, userId, onlyMy, selectedStatuses]);

    // Load available slots when not in month view and conditions are met
    React.useEffect(() => {
        // Only fetch available slots if:
        // 1. View is not month (day, week, or 3days)
        // 2. AND (non-admin user OR (admin with "only mine" OR admin with user filter))

        // Determine if we should fetch available slots
        const isAdminUser = user?.role === 'admin' || user?.role === 'Admin';
        const shouldFetchSlots = view !== "month" && (!isAdminUser || onlyMy || userId !== 0);

        if (!shouldFetchSlots) {
            setAvailableSlots([]);
            return;
        }

        // Create a unique key from current parameters to detect changes and prevent Strict Mode duplicates
        const currentParamsKey = JSON.stringify([
            view,
            currentDate.getTime(),
            userId,
            onlyMy,
            user?.role
        ]);

        // Skip if we've already loaded these exact parameters
        if (slotsParamsRef.current === currentParamsKey) {
            return;
        }

        slotsParamsRef.current = currentParamsKey;

        const loadAvailableSlots = async () => {
            try {
                // Determine the user_id to use for the request:
                // - If a specific user is selected (userId !== 0), use that
                // - Otherwise use the current user's ID
                const requestUserId = userId !== 0 ? userId : (typeof user.id === 'number' ? user.id : parseInt(user.id as string, 10));

                console.log(`[API] Fetching available slots for user ${requestUserId}`);

                // Calculate date range based on current view
                let fromDate: Date;
                let toDate: Date;

                if (view === "day") {
                    fromDate = new Date(currentDate);
                    fromDate.setHours(0, 0, 0, 0);
                    toDate = new Date(currentDate);
                    toDate.setHours(23, 59, 59, 999);
                } else if (view === "3days") {
                    fromDate = new Date(currentDate);
                    fromDate.setHours(0, 0, 0, 0);
                    toDate = new Date(currentDate);
                    toDate.setDate(toDate.getDate() + 2);
                    toDate.setHours(23, 59, 59, 999);
                } else if (view === "week") {
                    fromDate = getWeekStart(currentDate);
                    toDate = new Date(fromDate);
                    toDate.setDate(toDate.getDate() + 6);
                    toDate.setHours(23, 59, 59, 999);
                } else {
                    return;
                }

                const slots = await apiService.getAvailableSlots(
                    token,
                    requestUserId as number,
                    fromDate.getTime(),
                    toDate.getTime()
                );

                setAvailableSlots(slots || []);
            } catch (err: any) {
                console.error("Failed to load available slots:", err);
                setAvailableSlots([]);
            }
        };

        loadAvailableSlots();
    }, [view, currentDate, userId, onlyMy, user?.role, user.id, token]);

    // Get meetings for a specific day
    const getMeetingsForDay = (date: Date) => {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        return meetings
            .filter(meeting => {
                return meeting.start >= dayStart.getTime() && meeting.start <= dayEnd.getTime();
            })
            .sort((a, b) => a.start - b.start);
    };

    const formatTime = (timestamp: number) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", hour12: false });
    };

    // Generate tooltip for meeting
    const getMeetingTooltip = (meeting: Meeting) => {
        const user = users.find(u => u.id === meeting.user_id);
        const userName = user ? user.name : `User #${meeting.user_id}`;

        const meetingTypeName = getMeetingTypeName(meeting, meetingTypes);

        // First line: Meeting Type - Patient Type (if available)
        const firstLine = meeting.patient_type
            ? `${meetingTypeName} - ${meeting.patient_type}`
            : meetingTypeName;

        let tooltip = meeting.red_flag ? `🚩 Complex Patient\n${firstLine}` : firstLine;
        if (meeting.ad_hoc) {
            tooltip += `\nAdHoc`;
        }
        tooltip += `\nHM ID: ${meeting.HM_ID}`;
        tooltip += `\nTime: ${formatTime(meeting.start)} - ${formatTime(meeting.end)}`;
        tooltip += `\nTherapist: ${userName}`;

        if (meeting.status) {
            tooltip += `\nStatus: ${meeting.status}`;
        }

        if (meeting.warning && meeting.warning.trim()) {
            tooltip += `\n⚠️ Warning: ${meeting.warning}`;
        }

        if (meeting.comments && meeting.comments.trim()) {
            tooltip += `\nComments: ${meeting.comments}`;
        }

        return tooltip;
    };

    const goToPrev = () => {
        if (view === "month") {
            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
        } else if (view === "week") {
            const newDate = new Date(currentDate);
            newDate.setDate(newDate.getDate() - 7);
            setCurrentDate(newDate);
        } else if (view === "3days") {
            const newDate = new Date(currentDate);
            newDate.setDate(newDate.getDate() - 3);
            setCurrentDate(newDate);
        } else if (view === "day") {
            const newDate = new Date(currentDate);
            newDate.setDate(newDate.getDate() - 1);
            setCurrentDate(newDate);
        }
    };

    const goToNext = () => {
        if (view === "month") {
            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
        } else if (view === "week") {
            const newDate = new Date(currentDate);
            newDate.setDate(newDate.getDate() + 7);
            setCurrentDate(newDate);
        } else if (view === "3days") {
            const newDate = new Date(currentDate);
            newDate.setDate(newDate.getDate() + 3);
            setCurrentDate(newDate);
        } else if (view === "day") {
            const newDate = new Date(currentDate);
            newDate.setDate(newDate.getDate() + 1);
            setCurrentDate(newDate);
        }
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    // Handle meeting click (selection and menu)
    const handleMeetingClick = (e: React.MouseEvent, meetingId: number) => {
        // Ignore clicks on availability slots (negative IDs)
        if (meetingId < 0) return;

        // Select the meeting and open context menu on click
        e.stopPropagation();
        setSelectedMeetingId(meetingId);
        setContextMenu({ x: e.clientX, y: e.clientY, meetingId });
    };


    // Handle context menu actions
    const handleContextMenuAction = async (action: string) => {
        if (!contextMenu) return;

        const meeting = meetings.find(m => m.id === contextMenu.meetingId);
        if (!meeting) {
            setContextMenu(null);
            return;
        }

        // Close context menu first
        setContextMenu(null);


        // Handle "Start Team" (same as "Start" but explicit for meeting type 4)
        if (action === "Start Team" && meeting.team_url) {
            window.open(meeting.team_url, "_blank", "noopener,noreferrer");

            if (shouldStartRecallBot(meeting)) {
                try {
                    const token = localStorage.getItem('token');
                    const apiUrl = localStorage.getItem('apiUrl') || process.env.REACT_APP_API_URL || 'https://dev-backend.taliaz-cm.com';
                    const url = `${apiUrl}/start_meeting?token=${token}&meeting_id=${meeting.id}`;
                    fetch(url, { method: 'GET' });
                    console.log(`[PSY Meeting] Called start_meeting for meeting ${meeting.id}`);
                } catch (error) {
                    console.error('[PSY Meeting] Failed to call start_meeting:', error);
                }
            }

            onNavigate?.("patient", meeting.patient_id, meeting.id);
            return;
        }

        // Handle "Start Call" (call patient for meeting type 4 non-re_intake)
        if (action === "Start Call") {
            setSelectedMeetingForCall(meeting);
            return;
        }

        // Handle "Start"
        if (action === "Start" && meeting.team_url) {
            window.open(meeting.team_url, "_blank", "noopener,noreferrer");

            // For every video meeting (PSY 2/3, CM intake 1, CM re-intake 4), also call
            // start_meeting API so the transcription bot joins
            if (shouldStartRecallBot(meeting)) {
                try {
                    const token = localStorage.getItem('token');
                    const apiUrl = localStorage.getItem('apiUrl') || process.env.REACT_APP_API_URL || 'https://dev-backend.taliaz-cm.com';
                    const url = `${apiUrl}/start_meeting?token=${token}&meeting_id=${meeting.id}`;

                    fetch(url, { method: 'GET' });
                    console.log(`[PSY Meeting] Called start_meeting for meeting ${meeting.id}`);
                } catch (error) {
                    console.error('[PSY Meeting] Failed to call start_meeting:', error);
                }
            }

            onNavigate?.("patient", meeting.patient_id, meeting.id);
            return;
        }

        // Handle "View Meeting"
        if (action === "View Meeting") {
            onNavigate?.("patient", meeting.patient_id, meeting.id);
            return;
        }

        // Handle "Update in Yoman"
        if (action === "Update in Yoman" && meeting.yoman_url) {
            window.open(meeting.yoman_url, "_blank", "noopener,noreferrer");
            return;
        }

        // Handle "WhatsApp"
        if (action === "WhatsApp") {
            if (meeting.patient_phone) {
                setWaPopupPatient({
                    phone: normalizePhone(meeting.patient_phone),
                    name: meeting.patient_name,
                    id: meeting.patient_id,
                });
            }
            return;
        }

        // Handle "Call"
        if (action === "Call") {
            if (user.phone_ext) {
                const token = localStorage.getItem("token") || "";
                setStatusMessage({ type: 'loading', text: 'Initiating call...' });
                try {
                    const response = await apiService.makeCall(token, meeting.patient_id, meeting.patient_phone);
                    setStatusMessage({ type: 'success', text: response.message || 'Call initiated successfully' });
                    setTimeout(() => setStatusMessage(null), 3000);
                } catch (error: any) {
                    setStatusMessage({ type: 'error', text: error.message || 'Call failed' });
                    setTimeout(() => setStatusMessage(null), 5000);
                }
            } else {
                setSelectedMeetingForCall(meeting);
            }
            return;
        }

        // Handle prep status updates
        if (action === "Ready" || action === "No Answer" || action === "Need Prep") {
            const prepStatusValue = action === "Ready" ? "ready" : action === "No Answer" ? "no_answer" : "need_prep";
            setStatusMessage({ type: 'loading', text: `Updating prep status to ${action}...` });

            try {
                const token = localStorage.getItem("token") || "";
                const response = await apiService.updateMeetingPrepStatus(
                    token,
                    meeting.id,
                    prepStatusValue,
                    meeting.meetingtype_id,
                    clinicId,
                    onlyMy
                );

                // Update meetings in memory if response contains updated data
                if (response && Array.isArray(response)) {
                    setMeetings(prevMeetings => {
                        const updatedMeetings = [...prevMeetings];
                        response.forEach(updatedMeeting => {
                            if (updatedMeeting.op === 'update') {
                                const index = updatedMeetings.findIndex(m => m.id === updatedMeeting.id);
                                if (index !== -1) {
                                    updatedMeetings[index] = { ...updatedMeetings[index], ...updatedMeeting };
                                }
                            }
                        });
                        return updatedMeetings;
                    });
                }

                setStatusMessage({ type: 'success', text: `Prep status updated to ${action}` });
                setTimeout(() => setStatusMessage(null), 3000);
            } catch (error: any) {
                console.error(`Failed to update prep status:`, error);
                const errorMsg = error.response?.data?.message || error.message || 'An error occurred';
                setStatusMessage({ type: 'error', text: `Failed to update prep status: ${errorMsg}` });
                setTimeout(() => setStatusMessage(null), 5000);
            }
            return;
        }

        // Handle "No Show"
        if (action === "No Show") {
            setSelectedMeetingForNoShow(meeting);
            setIsNoShowModalOpen(true);
            return;
        }

        // Handle "Finish Treatment"
        if (action === "Finish Treatment") {
            setSelectedPatientIdForFinishTreatment(meeting.patient_id);
            setIsFinishTreatmentModalOpen(true);
            return;
        }

        // Handle "Cancel"
        if (action === "Cancel") {
            setSelectedMeetingForCancel(meeting);
            setIsCancelMeetingModalOpen(true);
            return;
        }

        // Handle "Edit Comments"
        if (action === "Edit Comments") {
            setSelectedMeetingForComments(meeting);
            setIsEditCommentsModalOpen(true);
            return;
        }
    };


    // Execute confirmed action
    const executeAction = async () => {
        if (!pendingAction) return;

        const meeting = meetings.find(m => m.id === pendingAction.meetingId);
        if (!meeting) {
            setPendingAction(null);
            return;
        }

        const actionText = pendingAction.action === "Cancel" ? "Canceling" : "Marking as no-show";
        setStatusMessage({ type: 'loading', text: `${actionText}...` });
        setPendingAction(null);

        try {
            const token = localStorage.getItem("token") || "";

            // Only handling No Show action now - Cancel is handled by modal
            if (pendingAction.action === "No Show") {
                await apiService.updateMeetingStatus({
                    token,
                    meeting_id: meeting.id,
                    status: "noshow",
                    source: "UI",
                    meetingtype_id: meeting.meetingtype_id,
                    reason_of_cancellation: ""
                });
                setStatusMessage({ type: 'success', text: 'Meeting marked as no-show successfully' });
            }

            // Refresh meetings list
            await refreshMeetings();

            // Clear success message after 3 seconds
            setTimeout(() => setStatusMessage(null), 3000);
        } catch (error: any) {
            console.error(`Failed to ${pendingAction.action} meeting:`, error);
            const errorMsg = error.response?.data?.message || error.message || 'An error occurred';
            setStatusMessage({ type: 'error', text: `Failed to ${pendingAction.action.toLowerCase()} meeting: ${errorMsg}` });

            // Clear error message after 5 seconds
            setTimeout(() => setStatusMessage(null), 5000);
        }
    };

    // Handle saving comments
    const handleSaveComments = async (comments: string) => {
        if (!selectedMeetingForComments) return;

        setIsSavingComments(true);
        setStatusMessage({ type: 'loading', text: 'Saving comments...' });

        try {
            const token = localStorage.getItem("token") || "";
            await apiService.updateCommentsMeeting({
                token,
                meeting_id: selectedMeetingForComments.id,
                comments: comments,
                source: "meetings",
                meetingtype_id: selectedMeetingForComments.meetingtype_id
            });

            setStatusMessage({ type: 'success', text: 'Comments saved successfully' });
            setTimeout(() => setStatusMessage(null), 3000);

            // Refresh meetings list
            await refreshMeetings();

            setIsEditCommentsModalOpen(false);
            setSelectedMeetingForComments(null);
        } catch (error: any) {
            console.error('Failed to save comments:', error);
            setStatusMessage({
                type: 'error',
                text: `Failed to save comments: ${error.response?.data?.message || error.message || 'Unknown error'}`
            });
            setTimeout(() => setStatusMessage(null), 5000);
        } finally {
            setIsSavingComments(false);
        }
    };

    // Close context menu when clicking outside
    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    // Close context menu on any click outside
    React.useEffect(() => {
        if (contextMenu) {
            document.addEventListener('click', handleCloseContextMenu);
            return () => {
                document.removeEventListener('click', handleCloseContextMenu);
            };
        }
    }, [contextMenu]);

    const monthName = currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });

    return (
        <Layout
            userName={user?.name || user?.email || "User"}
            onLogout={onLogout}
            activeMenuItem="calendar"
            onNavigate={onNavigate}
            institutionId={user.institution_id || 0}
            user={user}
            taskCount={taskCount}
            whatsappUnreadCount={whatsappUnreadCount}
        >
            <div style={{ height: "calc(100vh - 60px - 48px)", display: "flex", flexDirection: "column" }}>
                {/* Filters */}
                <div style={{
                    backgroundColor: "#fff",
                    padding: "12px 16px",
                    borderRadius: 8,
                    marginBottom: 16,
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    flexWrap: "wrap",
                    flexShrink: 0
                }}>
                    <select
                        value={meetingTypeId}
                        onChange={(e) => setMeetingTypeId(Number(e.target.value))}
                        style={{
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            fontSize: 14,
                            minWidth: 150
                        }}
                    >
                        <option value={0}>All Meeting Types</option>
                        {meetingTypes
                            .filter(mt => mt.active)
                            .map((mt) => (
                                <option key={mt.id} value={mt.id}>
                                    {mt.name}
                                </option>
                            ))}
                    </select>

                    <select
                        value={clinicId}
                        onChange={(e) => setClinicId(Number(e.target.value))}
                        style={{
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            fontSize: 14,
                            minWidth: 150
                        }}
                    >
                        <option value={0}>All Clinics</option>
                        {clinics.map((clinic: any) => (
                            <option key={clinic.id} value={clinic.id}>
                                {clinic.name}
                            </option>
                        ))}
                    </select>

                    {/* User filter - only show for admins */}
                    {(user?.role === 'admin' || user?.role === 'Admin') && (
                        <select
                            value={userId}
                            onChange={(e) => {
                                const selectedUserId = Number(e.target.value);
                                setUserId(selectedUserId);
                                if (selectedUserId !== 0) {
                                    setOnlyMy(false);
                                }
                            }}
                            style={{
                                padding: "8px 12px",
                                border: "1px solid #d1d5db",
                                borderRadius: 6,
                                fontSize: 14,
                                minWidth: 150
                            }}
                        >
                            <option value={0}>All Users</option>
                            {users
                                .filter(u => u.active && (u.type === 'case_manager' || u.type === 'doctor'))
                                .map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.name}
                                    </option>
                                ))}
                        </select>
                    )}

                    <div ref={statusMenuRef} style={{ position: "relative" }}>
                        <button
                            type="button"
                            onClick={() => setStatusMenuOpen((o) => !o)}
                            style={{
                                padding: "8px 12px",
                                border: "1px solid #d1d5db",
                                borderRadius: 6,
                                fontSize: 14,
                                minWidth: 150,
                                background: "#fff",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8
                            }}
                        >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {selectedStatuses.length === 0
                                    ? "All Statuses"
                                    : STATUS_OPTIONS.filter((o) => selectedStatuses.includes(o.value))
                                          .map((o) => o.label)
                                          .join(", ")}
                            </span>
                            <span style={{ fontSize: 10, color: "#6b7280" }}>▼</span>
                        </button>
                        {statusMenuOpen && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: "calc(100% + 4px)",
                                    left: 0,
                                    zIndex: 20,
                                    background: "#fff",
                                    border: "1px solid #d1d5db",
                                    borderRadius: 6,
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                    minWidth: 180,
                                    padding: 4
                                }}
                            >
                                <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", cursor: "pointer", fontSize: 14 }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedStatuses.length === 0}
                                        onChange={() => setSelectedStatuses([])}
                                    />
                                    All Statuses
                                </label>
                                {STATUS_OPTIONS.map((o) => (
                                    <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", cursor: "pointer", fontSize: 14 }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedStatuses.includes(o.value)}
                                            onChange={() => toggleStatus(o.value)}
                                        />
                                        {o.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Only my checkbox - only show for admins */}
                    {(user?.role === 'admin' || user?.role === 'Admin') && (
                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={onlyMy}
                                onChange={(e) => setOnlyMy(e.target.checked)}
                            />
                            <span style={{ fontSize: 14 }}>Only mine</span>
                        </label>
                    )}

                    <div style={{ flex: 1 }} />

                    {/* Month name and navigation */}
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{monthName}</h2>

                    {/* View buttons */}
                    <div style={{ display: "flex", gap: 4 }}>
                        {[
                            { key: "month", label: "Month" },
                            { key: "week", label: "Week" },
                            { key: "3days", label: "3 Days" },
                            { key: "day", label: "Day" }
                        ].map((v) => (
                            <button
                                data-ai-id="calendar-view-option"
                                key={v.key}
                                onClick={() => setView(v.key as any)}
                                style={{
                                    padding: "6px 12px",
                                    border: view === v.key ? "1px solid #3b5d6b" : "1px solid #d1d5db",
                                    backgroundColor: view === v.key ? "#3b5d6b" : "#fff",
                                    color: view === v.key ? "#fff" : "#000",
                                    borderRadius: 4,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    cursor: "pointer"
                                }}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={goToToday}
                        style={{
                            padding: "6px 16px",
                            border: "1px solid #d1d5db",
                            backgroundColor: "#fff",
                            borderRadius: 6,
                            fontSize: 14,
                            fontWeight: 500,
                            cursor: "pointer"
                        }}
                    >
                        Today
                    </button>

                    <button onClick={goToPrev} style={{ padding: "6px 12px", cursor: "pointer" }}>‹</button>
                    <button onClick={goToNext} style={{ padding: "6px 12px", cursor: "pointer" }}>›</button>
                </div>

                {/* Calendar Container - flex to take remaining space */}
                <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    {/* Calendar */}
                    {view === "month" && (
                        <div style={{ backgroundColor: "#fff", borderRadius: 8, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
                            {/* Day headers */}
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr)",
                                borderBottom: "1px solid #e5e7eb",
                                flexShrink: 0
                            }}>
                                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                                    <div key={day} style={{
                                        padding: 12,
                                        textAlign: "center",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: "#6b7280"
                                    }}>
                                        {day}
                                    </div>
                                ))}
                            </div>

                            {/* Calendar grid */}
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr)",
                                gridAutoRows: "minmax(120px, auto)",
                                flex: 1,
                                overflow: "auto"
                            }}>
                                {calendarDays.map((day, index) => {
                                    const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                                    const isToday = day.toDateString() === new Date().toDateString();
                                    const dayMeetings = getMeetingsForDay(day);

                                    return (
                                        <div
                                            key={index}
                                            style={{
                                                border: "1px solid #e5e7eb",
                                                padding: 8,
                                                backgroundColor: isCurrentMonth ? "#fff" : "#f9fafb",
                                                opacity: isCurrentMonth ? 1 : 0.5
                                            }}
                                        >
                                            <div style={{
                                                fontSize: 12,
                                                fontWeight: isToday ? 600 : 400,
                                                color: isToday ? "#3b5d6b" : "#000",
                                                marginBottom: 4
                                            }}>
                                                {day.getDate()}
                                            </div>

                                            <div style={{ fontSize: 10, overflow: "hidden" }}>
                                                {dayMeetings.slice(0, 3).map(meeting => (
                                                    <div
                                                        key={meeting.id}
                                                        onClick={(e) => handleMeetingClick(e, meeting.id)}
                                                        title={getMeetingTooltip(meeting)}
                                                        style={{
                                                            backgroundColor: meeting.color || "#e0e7ff",
                                                            padding: "2px 4px",
                                                            marginBottom: 2,
                                                            borderRadius: 2,
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            cursor: "pointer",
                                                            border: selectedMeetingId === meeting.id ? "2px solid #3b5d6b" : meeting.red_flag ? "5px solid #dc2626" : "2px solid transparent",
                                                            boxSizing: "border-box"
                                                        }}
                                                    >
                                                        {meeting.HM_ID} - {meeting.patient_name} {formatTime(meeting.start)} - {formatTime(meeting.end)}{meeting.ad_hoc ? " AdHoc" : ""}
                                                    </div>
                                                ))}
                                                {dayMeetings.length > 3 && (
                                                    <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                                                        +{dayMeetings.length - 3} more
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Weekly View */}
                    {view === "week" && (
                        <div style={{ backgroundColor: "#fff", borderRadius: 8, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
                            {/* Fixed Header */}
                            <div style={{ display: "grid", gridTemplateColumns: "60px repeat(7, 1fr)", minWidth: 800, flexShrink: 0 }}>
                                {/* Time column header */}
                                <div style={{ borderBottom: "1px solid #e5e7eb", padding: 12 }}></div>

                                {/* Day headers */}
                                {getWeekDays(getWeekStart(currentDate)).map((day, i) => {
                                    const isToday = day.toDateString() === new Date().toDateString();
                                    return (
                                        <div key={i} style={{
                                            borderBottom: "1px solid #e5e7eb",
                                            borderLeft: "1px solid #e5e7eb",
                                            padding: 12,
                                            textAlign: "center",
                                            fontWeight: isToday ? 600 : 500,
                                            color: isToday ? "#3b5d6b" : "#000",
                                            backgroundColor: "#fff"
                                        }}>
                                            <div style={{ fontSize: 12 }}>{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
                                            <div style={{ fontSize: 18 }}>{day.getDate()}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Scrollable Time Slots */}
                            <div style={{ overflow: "auto", flex: 1 }}>
                                <div style={{ display: "grid", gridTemplateColumns: "60px repeat(7, 1fr)", minWidth: 800 }}>

                                    {/* Time column with hour labels */}
                                    <div style={{ borderRight: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                                        {hours.map(hour => (
                                            <div key={hour} style={{
                                                borderTop: "1px solid #e5e7eb",
                                                padding: "8px 4px",
                                                fontSize: 10,
                                                color: "#6b7280",
                                                textAlign: "right",
                                                height: LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK
                                            }}>
                                                {hour.toString().padStart(2, "0")}:00
                                            </div>
                                        ))}
                                    </div>

                                    {/* Day cells with absolute positioned meetings */}
                                    {getWeekDays(getWeekStart(currentDate)).map((day) => {
                                        const dayKey = day.toDateString();
                                        const dayMeetings = positionedMeetingsForDays[dayKey] || [];
                                        const containerHeight = hours.length * LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK;

                                        return (
                                            <div key={dayKey} style={{
                                                borderLeft: "1px solid #e5e7eb",
                                                position: "relative",
                                                minHeight: containerHeight,
                                                backgroundColor: "#fff"
                                            }}>
                                                {/* Hour grid lines */}
                                                {hours.map(hour => (
                                                    <div key={hour} style={{
                                                        position: "absolute",
                                                        top: (hour - hours[0]) * LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK,
                                                        left: 0,
                                                        right: 0,
                                                        height: LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK,
                                                        borderTop: "1px solid #e5e7eb",
                                                        pointerEvents: "none"
                                                    }} />
                                                ))}

                                                {/* Positioned meetings */}
                                                {dayMeetings.map(meeting => (
                                                    <div
                                                        key={meeting.id}
                                                        onClick={(e) => handleMeetingClick(e, meeting.id)}
                                                        title={meeting.id >= 0 ? getMeetingTooltip(meeting) : undefined}
                                                        style={{
                                                            position: "absolute",
                                                            top: meeting.top,
                                                            left: `${meeting.left}%`,
                                                            width: `calc(${meeting.width}% - 4px)`,
                                                            height: meeting.height,
                                                            backgroundColor: meeting.color || "#e0e7ff",
                                                            padding: "4px 6px",
                                                            borderRadius: 3,
                                                            fontSize: 11,
                                                            overflow: "hidden",
                                                            cursor: meeting.id >= 0 ? "pointer" : "default",
                                                            border: selectedMeetingId === meeting.id ? "2px solid #3b5d6b" : meeting.red_flag ? "5px solid #dc2626" : "1px solid #000",
                                                            boxSizing: "border-box",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "flex-start"
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 600, fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                            {meeting.id >= 0
                                                                ? `${formatTime(meeting.start)} ${meeting.HM_ID} ${meeting.patient_name}${meeting.ad_hoc ? " AdHoc" : ""}`
                                                                : `${formatTime(meeting.start)} - ${formatTime(meeting.end)} Availability`
                                                            }
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 3 Days View */}
                    {view === "3days" && (
                        <div style={{ backgroundColor: "#fff", borderRadius: 8, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
                            {/* Fixed Header */}
                            <div style={{ display: "grid", gridTemplateColumns: "60px repeat(3, 1fr)", minWidth: 600, flexShrink: 0 }}>
                                {/* Time column header */}
                                <div style={{ borderBottom: "1px solid #e5e7eb", padding: 12 }}></div>

                                {/* Day headers */}
                                {getThreeDays(currentDate).map((day, i) => {
                                    const isToday = day.toDateString() === new Date().toDateString();
                                    return (
                                        <div key={i} style={{
                                            borderBottom: "1px solid #e5e7eb",
                                            borderLeft: "1px solid #e5e7eb",
                                            padding: 12,
                                            textAlign: "center",
                                            fontWeight: isToday ? 600 : 500,
                                            color: isToday ? "#3b5d6b" : "#000",
                                            backgroundColor: "#fff"
                                        }}>
                                            <div style={{ fontSize: 12 }}>{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
                                            <div style={{ fontSize: 18 }}>{day.getDate()}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Scrollable Time Slots */}
                            <div style={{ overflow: "auto", flex: 1 }}>
                                <div style={{ display: "grid", gridTemplateColumns: "60px repeat(3, 1fr)", minWidth: 600 }}>

                                    {/* Time column with hour labels */}
                                    <div style={{ borderRight: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                                        {hours.map(hour => (
                                            <div key={hour} style={{
                                                borderTop: "1px solid #e5e7eb",
                                                padding: "8px 4px",
                                                fontSize: 10,
                                                color: "#6b7280",
                                                textAlign: "right",
                                                height: LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK
                                            }}>
                                                {hour.toString().padStart(2, "0")}:00
                                            </div>
                                        ))}
                                    </div>

                                    {/* Day cells with absolute positioned meetings */}
                                    {getThreeDays(currentDate).map((day) => {
                                        const dayKey = day.toDateString();
                                        const dayMeetings = positionedMeetingsForDays[dayKey] || [];
                                        const containerHeight = hours.length * LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK;

                                        return (
                                            <div key={dayKey} style={{
                                                borderLeft: "1px solid #e5e7eb",
                                                position: "relative",
                                                minHeight: containerHeight,
                                                backgroundColor: "#fff"
                                            }}>
                                                {/* Hour grid lines */}
                                                {hours.map(hour => (
                                                    <div key={hour} style={{
                                                        position: "absolute",
                                                        top: (hour - hours[0]) * LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK,
                                                        left: 0,
                                                        right: 0,
                                                        height: LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK,
                                                        borderTop: "1px solid #e5e7eb",
                                                        pointerEvents: "none"
                                                    }} />
                                                ))}

                                                {/* Positioned meetings */}
                                                {dayMeetings.map(meeting => (
                                                    <div
                                                        key={meeting.id}
                                                        onClick={(e) => handleMeetingClick(e, meeting.id)}
                                                        title={meeting.id >= 0 ? getMeetingTooltip(meeting) : undefined}
                                                        style={{
                                                            position: "absolute",
                                                            top: meeting.top,
                                                            left: `${meeting.left}%`,
                                                            width: `calc(${meeting.width}% - 4px)`,
                                                            height: meeting.height,
                                                            backgroundColor: meeting.color || "#e0e7ff",
                                                            padding: "4px 6px",
                                                            borderRadius: 3,
                                                            fontSize: 11,
                                                            overflow: "hidden",
                                                            cursor: meeting.id >= 0 ? "pointer" : "default",
                                                            border: selectedMeetingId === meeting.id ? "2px solid #3b5d6b" : meeting.red_flag ? "5px solid #dc2626" : "1px solid #000",
                                                            boxSizing: "border-box",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "flex-start"
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 600, fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                            {meeting.id >= 0
                                                                ? `${formatTime(meeting.start)} ${meeting.HM_ID} ${meeting.patient_name}${meeting.ad_hoc ? " AdHoc" : ""}`
                                                                : `${formatTime(meeting.start)} - ${formatTime(meeting.end)} Availability`
                                                            }
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Daily View */}
                    {view === "day" && (
                        <div style={{ backgroundColor: "#fff", borderRadius: 8, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
                            {/* Fixed Header */}
                            <div style={{ display: "flex", justifyContent: "center", flexShrink: 0 }}>
                                <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", width: "60%" }}>
                                    {/* Day header */}
                                    <div style={{ borderBottom: "1px solid #e5e7eb", padding: 12 }}></div>
                                    <div style={{
                                        borderBottom: "1px solid #e5e7eb",
                                        borderLeft: "1px solid #e5e7eb",
                                        padding: 12,
                                        textAlign: "center",
                                        fontWeight: 600,
                                        fontSize: 16,
                                        backgroundColor: "#fff"
                                    }}>
                                        {currentDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                                    </div>
                                </div>
                            </div>

                            {/* Scrollable Time Slots */}
                            <div style={{ overflow: "auto", flex: 1, display: "flex", justifyContent: "center" }}>
                                <div style={{ width: "60%", borderLeft: "2px solid #3b5d6b", borderRight: "2px solid #3b5d6b", borderBottom: "2px solid #3b5d6b", overflow: "auto" }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "80px 1fr" }}>

                                        {/* Time column with hour labels */}
                                        <div style={{ borderRight: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                                            {hours.map(hour => (
                                                <div key={hour} style={{
                                                    borderTop: "1px solid #e5e7eb",
                                                    padding: "12px 8px",
                                                    fontSize: 12,
                                                    color: "#6b7280",
                                                    textAlign: "right",
                                                    fontWeight: 500,
                                                    height: LAYOUT_CONFIG.PIXELS_PER_HOUR_DAY
                                                }}>
                                                    {hour.toString().padStart(2, "0")}:00
                                                </div>
                                            ))}
                                        </div>

                                        {/* Day cell with absolute positioned meetings */}
                                        {(() => {
                                            const dayKey = currentDate.toDateString();
                                            const dayMeetings = positionedMeetingsForDays[dayKey] || [];
                                            const containerHeight = hours.length * LAYOUT_CONFIG.PIXELS_PER_HOUR_DAY;

                                            return (
                                                <div style={{
                                                    borderLeft: "1px solid #e5e7eb",
                                                    position: "relative",
                                                    minHeight: containerHeight,
                                                    backgroundColor: "#fff"
                                                }}>
                                                    {/* Hour grid lines */}
                                                    {hours.map(hour => (
                                                        <div key={hour} style={{
                                                            position: "absolute",
                                                            top: (hour - hours[0]) * LAYOUT_CONFIG.PIXELS_PER_HOUR_DAY,
                                                            left: 0,
                                                            right: 0,
                                                            height: LAYOUT_CONFIG.PIXELS_PER_HOUR_DAY,
                                                            borderTop: "1px solid #e5e7eb",
                                                            pointerEvents: "none"
                                                        }} />
                                                    ))}

                                                    {/* Positioned meetings */}
                                                    {dayMeetings.map(meeting => {
                                                        const isAvailability = meeting.id < 0;
                                                        const meetingTypeName = isAvailability ? "" : getMeetingTypeName(meeting, meetingTypes);
                                                        const capitalizedStatus = isAvailability ? "" : (meeting.status ? meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1) : '');

                                                        return (
                                                            <div
                                                                key={meeting.id}
                                                                onClick={(e) => handleMeetingClick(e, meeting.id)}
                                                                title={isAvailability ? undefined : getMeetingTooltip(meeting)}
                                                                style={{
                                                                    position: "absolute",
                                                                    top: meeting.top,
                                                                    left: `${meeting.left}%`,
                                                                    width: `calc(${meeting.width}% - 4px)`,
                                                                    height: meeting.height,
                                                                    backgroundColor: meeting.color || "#e0e7ff",
                                                                    padding: "8px 12px",
                                                                    borderRadius: 4,
                                                                    fontSize: 13,
                                                                    overflow: "hidden",
                                                                    cursor: isAvailability ? "default" : "pointer",
                                                                    border: selectedMeetingId === meeting.id ? "2px solid #3b5d6b" : meeting.red_flag ? "5px solid #dc2626" : "1px solid #000",
                                                                    boxSizing: "border-box",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "flex-start"
                                                                }}
                                                            >
                                                                <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                    {isAvailability
                                                                        ? `${formatTime(meeting.start)} - ${formatTime(meeting.end)} Availability`
                                                                        : `${formatTime(meeting.start)} - ${formatTime(meeting.end)} ${meeting.HM_ID} ${meeting.patient_name} - ${meetingTypeName} (${capitalizedStatus})${meeting.ad_hoc ? " AdHoc" : ""}`
                                                                    }
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Status bar - shows loading, errors, and action status */}
                    {(loading || error || statusMessage) && (
                        <div style={{
                            flexShrink: 0,
                            marginTop: 8,
                            padding: "6px 12px",
                            backgroundColor:
                                error || statusMessage?.type === 'error' ? "#fee2e2" :
                                    statusMessage?.type === 'success' ? "#dcfce7" :
                                        "#f3f4f6",
                            borderRadius: 6,
                            fontSize: 12,
                            textAlign: "center",
                            color:
                                error || statusMessage?.type === 'error' ? "#dc2626" :
                                    statusMessage?.type === 'success' ? "#16a34a" :
                                        "#6b7280"
                        }}>
                            {loading && "Loading..."}
                            {error && error}
                            {statusMessage && statusMessage.text}
                        </div>
                    )}
                </div>

                {/* Context Menu */}
                {contextMenu && (() => {
                    const meeting = meetings.find(m => m.id === contextMenu.meetingId);
                    const showPrepOptions = user.show_prep_meeting && meeting?.status?.toLowerCase() === 'active';
                    const prepStatus = meeting?.prep_status?.toLowerCase();

                    return (
                        <ContextMenu
                            x={contextMenu.x}
                            y={contextMenu.y}
                            options={[
                                ...(meeting?.meetingtype_id === 4 && !meeting?.re_intake ? [
                                    {
                                        label: 'Start Team',
                                        icon: '▶️',
                                        onClick: () => handleContextMenuAction('Start Team')
                                    },
                                ] : [
                                    {
                                        label: 'Start',
                                        icon: '▶️',
                                        onClick: () => handleContextMenuAction('Start')
                                    }
                                ]),
                                {
                                    label: 'View Meeting',
                                    icon: '👁️',
                                    onClick: () => handleContextMenuAction('View Meeting')
                                },
                                {
                                    label: 'Update in Yoman',
                                    icon: '📅',
                                    onClick: () => handleContextMenuAction('Update in Yoman')
                                },
                                {
                                    label: 'Call',
                                    icon: '📞',
                                    onClick: () => handleContextMenuAction('Call')
                                },
                                ...(meeting?.patient_phone ? [{
                                    label: 'WhatsApp',
                                    icon: '💬',
                                    onClick: () => handleContextMenuAction('WhatsApp')
                                }] : []),
                                // Prep meeting options - conditionally shown
                                ...(showPrepOptions && prepStatus !== 'ready' ? [{
                                    label: 'Ready',
                                    icon: '✅',
                                    onClick: () => handleContextMenuAction('Ready')
                                }] : []),
                                ...(showPrepOptions && prepStatus !== 'no_answer' ? [{
                                    label: 'No Answer',
                                    icon: '📵',
                                    onClick: () => handleContextMenuAction('No Answer')
                                }] : []),
                                ...(showPrepOptions && prepStatus !== 'need_prep' ? [{
                                    label: 'Need Prep',
                                    icon: '📋',
                                    onClick: () => handleContextMenuAction('Need Prep')
                                }] : []),
                                ...(meeting?.status !== "noshow" ? [{
                                    label: 'No Show',
                                    icon: '⛔',
                                    onClick: () => handleContextMenuAction('No Show')
                                }] : []),
                                {
                                    label: 'Finish Treatment',
                                    icon: (
                                        <svg fill="#e23232" width="16px" height="16px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
                                        </svg>
                                    ),
                                    iconClassName: '',
                                    onClick: () => handleContextMenuAction('Finish Treatment')
                                },
                                {
                                    label: 'Cancel',
                                    icon: '🚫',
                                    onClick: () => handleContextMenuAction('Cancel')
                                },
                                {
                                    label: 'Edit Comments',
                                    icon: '💬',
                                    onClick: () => handleContextMenuAction('Edit Comments')
                                }
                            ]}
                            onClose={() => setContextMenu(null)}
                        />
                    );
                })()}

                {/* Confirmation Dialog */}
                {pendingAction && (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2000
                        }}
                        onClick={() => setPendingAction(null)}
                    >
                        <div
                            style={{
                                backgroundColor: "#fff",
                                borderRadius: 8,
                                padding: 24,
                                maxWidth: 400,
                                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)"
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 18, fontWeight: 600 }}>
                                Confirm {pendingAction.action}
                            </h3>
                            <p style={{ margin: 0, marginBottom: 20, color: "#6b7280", fontSize: 14 }}>
                                Are you sure you want to {pendingAction.action.toLowerCase()} this meeting?
                            </p>
                            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                                <button
                                    onClick={() => setPendingAction(null)}
                                    style={{
                                        padding: "8px 16px",
                                        border: "1px solid #d1d5db",
                                        backgroundColor: "#fff",
                                        borderRadius: 6,
                                        fontSize: 14,
                                        cursor: "pointer",
                                        fontWeight: 500
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={executeAction}
                                    style={{
                                        padding: "8px 16px",
                                        border: "none",
                                        backgroundColor: pendingAction.action === "Cancel" ? "#dc2626" : "#ea580c",
                                        color: "#fff",
                                        borderRadius: 6,
                                        fontSize: 14,
                                        cursor: "pointer",
                                        fontWeight: 500
                                    }}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Finish Treatment Modal */}
                {selectedPatientIdForFinishTreatment !== null && (
                    <FinishTreatmentModal
                        isOpen={isFinishTreatmentModalOpen}
                        onClose={() => {
                            setIsFinishTreatmentModalOpen(false);
                            setSelectedPatientIdForFinishTreatment(null);
                        }}
                        patientId={selectedPatientIdForFinishTreatment}
                        token={token}
                        onSuccess={async () => {
                            // Reload meetings after finishing treatment
                            try {
                                setStatusMessage({ type: 'loading', text: 'Reloading meetings...' });
                                let fromDate = new Date(currentDate);
                                let toDate = new Date(currentDate);

                                if (view === 'day') {
                                    fromDate.setHours(0, 0, 0, 0);
                                    toDate.setHours(23, 59, 59, 999);
                                } else if (view === 'week') {
                                    const getWeekStart = (date: Date) => {
                                        const d = new Date(date);
                                        const day = d.getDay();
                                        const diff = d.getDate() - day;
                                        return new Date(d.setDate(diff));
                                    };
                                    fromDate = getWeekStart(currentDate);
                                    toDate = new Date(fromDate);
                                    toDate.setDate(toDate.getDate() + 6);
                                    toDate.setHours(23, 59, 59, 999);
                                } else { // month view
                                    const year = currentDate.getFullYear();
                                    const month = currentDate.getMonth();
                                    fromDate = new Date(year, month, 1);
                                    fromDate.setDate(fromDate.getDate() - fromDate.getDay());
                                    toDate = new Date(year, month + 1, 0);
                                    toDate.setDate(toDate.getDate() + (6 - toDate.getDay()));
                                    toDate.setHours(23, 59, 59, 999);
                                }

                                const response = await apiService.getMeetingsByDates({
                                    token,
                                    meeting_type_id: meetingTypeId,
                                    clinic_id: clinicId,
                                    user_id: userId,
                                    only_my: onlyMy,
                                    from_date: fromDate.getTime(),
                                    to_date: toDate.getTime(),
                                    status: selectedStatuses,
                                });
                                setMeetings(response);
                                setStatusMessage({ type: 'success', text: 'Meetings reloaded successfully' });
                                setTimeout(() => setStatusMessage(null), 3000);
                            } catch (err: any) {
                                console.error('Failed to reload meetings:', err);
                                setStatusMessage({ type: 'error', text: 'Failed to reload meetings' });
                                setTimeout(() => setStatusMessage(null), 5000);
                            }
                        }}
                    />
                )}

                {/* Edit Comments Modal */}
                {selectedMeetingForComments && (
                    <EditCommentsModal
                        isOpen={isEditCommentsModalOpen}
                        onClose={() => {
                            setIsEditCommentsModalOpen(false);
                            setSelectedMeetingForComments(null);
                        }}
                        onSave={handleSaveComments}
                        currentComments={selectedMeetingForComments.comments || ""}
                        isSaving={isSavingComments}
                    />
                )}

                {/* No Show Modal */}
                {selectedMeetingForNoShow && (
                    <NoShowModal
                        isOpen={isNoShowModalOpen}
                        onClose={() => {
                            setIsNoShowModalOpen(false);
                            setSelectedMeetingForNoShow(null);
                        }}
                        meetingId={selectedMeetingForNoShow.id}
                        meetingTypeId={selectedMeetingForNoShow.meetingtype_id}
                        token={token}
                        onSuccess={async () => {
                            setStatusMessage({ type: 'success', text: 'Meeting marked as no-show successfully' });
                            setTimeout(() => setStatusMessage(null), 3000);
                            await refreshMeetings();
                        }}
                    />
                )}

                {/* WhatsApp Popup */}
                {waPopupPatient && (
                    <WhatsAppConversationPopup
                        phoneNumber={waPopupPatient.phone}
                        patientName={waPopupPatient.name}
                        patientId={waPopupPatient.id}
                        currentUserId={typeof user.id === "number" ? user.id : Number(user.id)}
                        onClose={() => setWaPopupPatient(null)}
                    />
                )}

                {/* Call Patient Modal */}
                {selectedMeetingForCall && (
                    <CallPatientModal
                        meeting={selectedMeetingForCall}
                        token={token}
                        onClose={() => setSelectedMeetingForCall(null)}
                        onSuccess={() => {
                            setStatusMessage({ type: 'success', text: 'Call initiated successfully' });
                            setTimeout(() => setStatusMessage(null), 3000);
                            onNavigate?.("patient", selectedMeetingForCall.patient_id, selectedMeetingForCall.id);
                        }}
                        onError={(msg) => {
                            setStatusMessage({ type: 'error', text: msg });
                            setTimeout(() => setStatusMessage(null), 5000);
                        }}
                    />
                )}

                {/* Cancel Meeting Modal */}
                {selectedMeetingForCancel && (
                    <CancelMeetingModal
                        isOpen={isCancelMeetingModalOpen}
                        onClose={() => {
                            setIsCancelMeetingModalOpen(false);
                            setSelectedMeetingForCancel(null);
                        }}
                        meetingId={selectedMeetingForCancel.id}
                        meetingTypeId={selectedMeetingForCancel.meetingtype_id}
                        token={token}
                        onSuccess={async () => {
                            setStatusMessage({ type: 'success', text: 'Meeting canceled successfully' });
                            setTimeout(() => setStatusMessage(null), 3000);
                            await refreshMeetings();
                        }}
                    />
                )}
            </div>
        </Layout >
    );
};

const EditCommentsModal: React.FC<EditCommentsModalProps> = ({
    isOpen,
    onClose,
    onSave,
    currentComments,
    isSaving
}) => {
    const [comments, setComments] = useState(currentComments);

    useEffect(() => {
        setComments(currentComments);
    }, [currentComments]);

    if (!isOpen) return null;

    const handleSave = async () => {
        await onSave(comments);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1300]">
            <div
                className="bg-[#B8DDD0] rounded-3xl p-6 shadow-2xl max-w-md w-full mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-6">
                    <h2 className={POPUP_STYLES.title}>Edit Comments</h2>
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="text-gray-600 hover:text-gray-800 text-2xl font-light disabled:opacity-50"
                    >
                        ✕
                    </button>
                </div>

                <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">
                        Comment
                    </label>
                    <input
                        type="text"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Enter value"
                        disabled={isSaving}
                        className={POPUP_STYLES.input}
                    />
                </div>

                <div className="flex gap-4 justify-center">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        Save
                    </button>
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};
